import { useCallback, useEffect, useState, useRef } from 'react'
import { addNotification } from 'containers/Notifications/notificationsSlice'
import { STATUS, UNAUTHORIZED_TYPES } from 'utils/constants'
import { useDispatch, useSelector } from 'react-redux'
import useTranslation from 'hooks/useTranslation'
import { newsmscode, verify } from 'rest/urls'
import Submit from 'components/inputs/Submit'
import { toPhoneString } from 'utils/string'
import Popup from 'components/popups/Popup'
import { selectForm } from '../formSlice'
import { POST } from 'rest/request'

const INIT_CODE = { 0: '', 1: '', 2: '', 3: '', 4: '', 5: '' }
const CODE_LENGTH = 6
const RESEND_COOLDOWN = 60_000

const VerificationPopup = ({ loginUser, close }) => {
  const { phone = '', prefix } = useSelector(selectForm)
  const [status, setStatus] = useState(STATUS.idle)
  const [warning, setWarning] = useState(false)
  const [code, setCode] = useState(INIT_CODE)
  const [isResending, setResending] = useState(false)

  const refs = []
  const autoCodeLoaderRef = useRef(null)
  const sentDate = useRef(Date.now())
  const dispatch = useDispatch()
  const codeToString = Object.entries(code).reduce((r, [, v]) => r += v, '')

  const { t } = useTranslation('auth')
  const validateCode = useCallback(async () => {
    setStatus(STATUS.pending)
    if (autoCodeLoaderRef.current) {
      autoCodeLoaderRef.current?.abort()
      autoCodeLoaderRef.current = null
    }

    try {
      const body = { prefix, phone, token: codeToString }
      const response = await POST(verify(UNAUTHORIZED_TYPES.phone), { body })

      if (response) {
        setStatus(STATUS.succeed)
        loginUser()
      }

    } catch (err) {
      setStatus(STATUS.failed)
    }
  }, [codeToString, loginUser, phone, prefix])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => refs[0] && refs[0]?.focus(), [])
  useEffect(() => {
    if ('OTPCredential' in window) {
      const ac = new AbortController()
      autoCodeLoaderRef.current = ac
      navigator.credentials.get({
        otp: { transport: ['sms'] },
        signal: ac.signal
      }).then(otp => {
        if (otp.code) {
          const codeList = otp.code.split('')
          setCode({ ...codeList })
          validateCode()
        }
      }).catch(err => {
        console.warn(err)
      })

      return () => {
        ac.abort()
      }
    }
  }, [validateCode])

  const resendCode = async () => {
    if (status === STATUS.pending || isResending) return
    if (Date.now() - sentDate.current < RESEND_COOLDOWN) {
      dispatch(addNotification({ type: 'error', message: t('resendCooldown') }))
      return
    }

    setResending(true)
    try {
      const response = await POST(newsmscode, { body: { prefix, phone } })
      if (!response) throw new Error('Wrong response')
      sentDate.current = Date.now()
      setCode(INIT_CODE)
      setWarning(false)
      setStatus(STATUS.idle)
      refs[0]?.focus()
    } catch (err) {
      console.error('Code is not sent!', err)
    } finally {
      setResending(false)
    }
  }

  const getSubmitText = len => len === CODE_LENGTH
    ? t('authorize')
    : `${CODE_LENGTH - len} ${t(len === CODE_LENGTH - 1 ? 'characterIsMissing' : 'charactersAreMissing')}`

  const moveFocus = (ref, forward = true) => {
    const step = forward ? 1 : -1
    const index = refs.findIndex(r => Object.is(r, ref))
    const key = index > -1 ? index + step : undefined

    if (key !== undefined && refs[key]) {
      const len = code[key].length
      refs[key].setSelectionRange(0, len)
      refs[key].focus()
    }
  }

  return (
    <Popup close={close}>
      <div className='popup__verification'>
        <i className='icon icon--logo s24' />
        <h2>{t('authorization')}</h2>

        <div className='verification__text'>
          <div>{t('rewriteTheVerificationCode')}</div>
          <div>{t('sentToNumber')}:</div>
        </div>

        <div className='verification__phone'>
          <h2>{toPhoneString(phone, prefix)}</h2>
        </div>

        <div className='center'>
          <div className={`verification__code group${warning ? ' --warning' : ''}`}>
            {Object.entries(code).map(([k, v]) => <input
              required
              key={k}
              value={v}
              type='text'
              inputMode='numeric'
              ref={r => refs.push(r)}
              disabled={status === STATUS.pending}
              onKeyUp={e => {
                if (e.key === 'Backspace' && !v) {
                  moveFocus(e.target, false)
                } else if (e.key === 'Enter' && codeToString.length === CODE_LENGTH) {
                  validateCode()
                }
              }}
              onChange={e => {
                const { value } = e.target
                const firstChar = value.charAt(0)

                if (value.length === CODE_LENGTH && value.match(/^[0-9]{6}$/)) {
                  const list = value.split('')
                  setCode({ ...list })
                }
                else if (!firstChar || firstChar.match(/^[0-9]{1}$/)) {
                  setCode({
                    ...code,
                    [k]: firstChar
                  })
                  moveFocus(e.target, !!firstChar)
                }
              }}
            />)}
          </div>
        </div>

        <div
          className='verification__button'
          onClick={resendCode}>
          {t(isResending ? 'loading' : 'sendAgain')}
        </div>

        <Submit
          text={getSubmitText(codeToString.length)}
          block={codeToString.length !== CODE_LENGTH || status === STATUS.pending}
          action={validateCode}
        />
      </div>
    </Popup>
  )
}

export default VerificationPopup